/**
 * Unified attachment client (issue #306).
 *
 * One place for every attachment HTTP call and the per-file URL/type helpers
 * that used to be redefined in each feature module. Generic list/upload/delete
 * go through the registry-backed `/api/v1/entities/:entityType/:entityId/attachments`
 * endpoints (#303); asset photo ordering, cover selection, photo sharing, and
 * manual-page collections have no generic equivalent and keep wrapping the
 * legacy `/api/v1/assets/:id/*` routes.
 *
 * Types come from the `@household/shared/attachments` subpath — never the root
 * barrel, which drags csv-parse/Buffer into the browser bundle.
 */
import type {
  AttachableEntityType,
  AttachmentCollectionType,
  AttachmentVersionDiffResponse,
  AttachmentVersionListResponse,
  DocumentCollectionType
} from "@household/shared/attachments";
import { defaultAttachmentCollectionType, documentCollectionTypes } from "@household/shared/attachments";
import type { Asset, Attachment, AttachmentVision, EntityReferenceId } from "../app/types.js";
import { api, deleteJson, patchJson, postJson } from "./api.js";
import { runWithConcurrency, type UploadOutcome } from "./uploadQueue.js";

export type {
  AttachableEntityType,
  AttachmentCollectionType,
  DocumentCollectionType,
  AttachmentCurrentVersionDto,
  AttachmentDiffHunk,
  AttachmentVersionDiffResponse,
  AttachmentVersionDto,
  AttachmentVersionListResponse
} from "@household/shared/attachments";

// ---------------------------------------------------------------------------
// URL + preview helpers (shared by every surface)
// ---------------------------------------------------------------------------

export function attachmentFileUrl(attachment: Attachment): string {
  return attachment.fileUrl || `/api/v1/attachments/${attachment.id}/file`;
}

export function attachmentPreviewUrl(attachment: Attachment): string {
  return attachment.previewUrl || attachment.thumbnailUrl || attachmentFileUrl(attachment);
}

/** List rows show a small image, so prefer the generated thumbnail over the
 * full-size preview to avoid downloading originals for every image row. */
export function attachmentThumbUrl(attachment: Attachment): string {
  return attachment.thumbnailUrl || attachment.previewUrl || attachmentFileUrl(attachment);
}

export function canPreviewAttachment(attachment: Attachment): boolean {
  return Boolean(attachment.previewUrl || attachment.thumbnailUrl || attachment.mimeType.toLowerCase().startsWith("image/"));
}

/** The AI vision block (#260/#261) for an image attachment, or undefined when
 * there is no usable description/labels/OCR — a non-image, an image still in the
 * enrichment queue, or one that failed enrichment. An EDITED block passes even
 * when empty (#555: "user removed bad AI text" still needs the reset affordance). */
export function attachmentVision(attachment: Attachment): AttachmentVision | undefined {
  const vision = attachment.vision;
  if (!vision) return undefined;
  if (!vision.description && !vision.ocrText && (vision.labels?.length ?? 0) === 0 && !vision.edited) return undefined;
  return vision;
}

/** Correct an AI image description (#555). Sends only the provided fields; the
 * server keeps current values for the rest and returns the fresh DTO. */
export function updateAttachmentVision(
  attachmentId: number,
  changes: { description?: string; labels?: string[]; ocrText?: string }
): Promise<Attachment> {
  return patchJson<Attachment>(`/api/v1/attachments/${attachmentId}/vision`, changes);
}

/** Drop the human correction and restore the AI-generated text (#555). */
export function resetAttachmentVision(attachmentId: number): Promise<Attachment> {
  return patchJson<Attachment>(`/api/v1/attachments/${attachmentId}/vision`, { reset: true });
}

export function isPdfAttachment(attachment: Attachment): boolean {
  return attachment.mimeType.toLowerCase() === "application/pdf";
}

/** UI gate for the version Compare affordance (#307): only text/* files can
 * be diffed (the server re-checks with magic-byte sniffing and answers 415
 * for anything else). */
export function isTextMime(mimeType: string): boolean {
  return (mimeType.split(";")[0] ?? "").trim().toLowerCase().startsWith("text/");
}

/**
 * Is this id safe to interpolate into an attachment URL? (#982)
 *
 * A parent create that resolved without a real record — an offline-queued write
 * (`{__offlineQueued:true}`), a stubbed response — leaves the id `undefined`,
 * and template interpolation turns that into the literal path segment
 * "undefined". The request then 404s (or, worse, matches some other route) and
 * the failure is reported against a parent the user never sees. Every caller
 * that builds an attachment URL from a just-created parent checks this first.
 */
// Takes `unknown`: the id being checked comes off an untyped API response, so
// the declared `EntityReferenceId` is a claim this function exists to verify.
// The `typeof` test is load-bearing, not belt-and-braces — stringifying first
// would let `{}` through as "[object Object]" and `false` through as "false",
// both of which interpolate into a URL just as happily as "undefined" did.
export function isResolvedEntityId(id: unknown): id is EntityReferenceId {
  if (typeof id !== "string") return false;
  const text = id.trim();
  return text !== "" && text !== "undefined" && text !== "null" && text !== "NaN";
}

// ---------------------------------------------------------------------------
// Generic registry-backed endpoints (#303)
// ---------------------------------------------------------------------------

export function listAttachments(entityType: AttachableEntityType, entityId: EntityReferenceId): Promise<Attachment[]> {
  return api<Attachment[]>(`/api/v1/entities/${entityType}/${entityId}/attachments`);
}

/**
 * Put the declared kind on the wire, ahead of the file (#1176).
 *
 * The order is load-bearing, not tidiness: busboy only exposes fields it has
 * already parsed, so a `collectionType` appended after the file part is
 * invisible to the route and the upload is refused as untyped. `uploadVersion`
 * says the same thing about `changeNote`.
 *
 * Every caller declares a kind — the default is the same `other` an
 * undeclared upload used to become, so nothing about a chore's or a project's
 * attachments changes — because the SERVER refuses a document that says
 * nothing, and "the app's own screens always say something" is what keeps that
 * rule from being a surprise 400 in a surface with no picker.
 */
export function attachmentUploadBody(file: File, collectionType: AttachmentCollectionType = defaultAttachmentCollectionType): FormData {
  const body = new FormData();
  body.append("collectionType", collectionType);
  body.append("file", file);
  return body;
}

export function uploadAttachment(
  entityType: AttachableEntityType,
  entityId: EntityReferenceId,
  file: File,
  collectionType: AttachmentCollectionType = defaultAttachmentCollectionType
): Promise<Attachment> {
  return api<Attachment>(`/api/v1/entities/${entityType}/${entityId}/attachments`, {
    method: "POST",
    body: attachmentUploadBody(file, collectionType)
  });
}

/**
 * The outcome of uploading a batch of files (#522).
 *
 * `results` is index-aligned with the input files — null where that file
 * failed — because callers map positions back to intent (which photo is the
 * cover, which folder a file belongs in). `uploaded` is the compacted list for
 * callers that only care about what landed.
 */
export type AttachmentUploadBatch = {
  results: Array<Attachment | null>;
  uploaded: Attachment[];
  failed: Array<{ file: File; reason: unknown }>;
};

function toUploadBatch(outcomes: Array<UploadOutcome<Attachment>>): AttachmentUploadBatch {
  const results = outcomes.map((outcome) => (outcome.status === "fulfilled" ? outcome.value : null));
  return {
    results,
    uploaded: results.filter((entry): entry is Attachment => entry != null),
    failed: outcomes
      .filter((outcome) => outcome.status === "rejected")
      .map((outcome) => ({ file: outcome.file, reason: (outcome as { reason: unknown }).reason }))
  };
}

export async function uploadAttachments(
  entityType: AttachableEntityType,
  entityId: EntityReferenceId,
  files: File[],
  options: { limit?: number; collectionType?: AttachmentCollectionType } = {}
): Promise<AttachmentUploadBatch> {
  const outcomes = await runWithConcurrency(
    files,
    (file) => uploadAttachment(entityType, entityId, file, options.collectionType),
    options
  );
  return toUploadBatch(outcomes);
}

export function deleteAttachment(attachmentId: number): Promise<{ ok: true }> {
  return deleteJson<{ ok: true }>(`/api/v1/attachments/${attachmentId}`);
}

/** Version chain (#305/#307). List powers the Versions modal; upload/restore
 * return the FRESH version list so the UI never re-fetches after a mutation. */
export function listAttachmentVersions(attachmentId: number): Promise<AttachmentVersionListResponse> {
  return api<AttachmentVersionListResponse>(`/api/v1/attachments/${attachmentId}/versions`);
}

export function uploadVersion(attachmentId: number, file: File, changeNote?: string): Promise<AttachmentVersionListResponse> {
  const body = new FormData();
  // Order matters: multipart fields are only visible to the server when they
  // arrive BEFORE the file part.
  if (changeNote?.trim()) body.append("changeNote", changeNote.trim());
  body.append("file", file);
  return api<AttachmentVersionListResponse>(`/api/v1/attachments/${attachmentId}/versions`, { method: "POST", body });
}

export function restoreVersion(attachmentId: number, toVersion: number, changeNote?: string): Promise<AttachmentVersionListResponse> {
  return postJson<AttachmentVersionListResponse>(`/api/v1/attachments/${attachmentId}/restore`, {
    toVersion,
    ...(changeNote?.trim() ? { changeNote: changeNote.trim() } : {})
  });
}

/** Unified diff between two TEXT versions; the hunks feed <TextDiff> directly. */
export function diffVersions(attachmentId: number, fromVersion: number, toVersion: number): Promise<AttachmentVersionDiffResponse> {
  return api<AttachmentVersionDiffResponse>(`/api/v1/attachments/${attachmentId}/versions/${fromVersion}/diff/${toVersion}`);
}

// ---------------------------------------------------------------------------
// Manual-page collection grouping (asset manuals)
// ---------------------------------------------------------------------------

export type ManualAttachmentGroup = {
  id: string;
  label: string;
  attachments: Attachment[];
  createdAt: string;
  sizeBytes: number;
};

export type AttachmentDisplayRow =
  | { id: string; kind: "manual"; group: ManualAttachmentGroup }
  | { id: string; kind: "file"; attachment: Attachment };

export function isManualPageAttachment(attachment: Attachment): boolean {
  return attachment.collectionType === "manual_page" && Boolean(attachment.collectionId);
}

export function groupManualAttachments(attachments: Attachment[]): ManualAttachmentGroup[] {
  const groups = new Map<string, ManualAttachmentGroup>();
  for (const attachment of attachments) {
    if (!isManualPageAttachment(attachment) || !attachment.collectionId) continue;
    const label = attachment.collectionLabel?.trim() || "Manual pages";
    const existing = groups.get(attachment.collectionId);
    if (existing) {
      existing.attachments.push(attachment);
      existing.sizeBytes += attachment.sizeBytes;
      if (attachment.createdAt > existing.createdAt) existing.createdAt = attachment.createdAt;
    } else {
      groups.set(attachment.collectionId, {
        id: attachment.collectionId,
        label,
        attachments: [attachment],
        createdAt: attachment.createdAt,
        sizeBytes: attachment.sizeBytes
      });
    }
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      attachments: [...group.attachments].sort((a, b) => (a.collectionOrder ?? 0) - (b.collectionOrder ?? 0) || a.id - b.id)
    }))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Manual-page collections collapse into one row apiece; every other
 * attachment stays a standalone file row. */
export function buildAttachmentRows(attachments: Attachment[]): AttachmentDisplayRow[] {
  const manualGroups = groupManualAttachments(attachments);
  const manualIds = new Set(manualGroups.flatMap((group) => group.attachments.map((attachment) => attachment.id)));
  return [
    ...manualGroups.map((group): AttachmentDisplayRow => ({ id: `manual-${group.id}`, kind: "manual", group })),
    ...attachments
      .filter((attachment) => !manualIds.has(attachment.id))
      .map((attachment): AttachmentDisplayRow => ({ id: `file-${attachment.id}`, kind: "file", attachment }))
  ];
}

// ---------------------------------------------------------------------------
// Asset photo helpers + legacy asset routes (no generic equivalent)
// ---------------------------------------------------------------------------

export function isImageFile(file: File): boolean {
  return file.type.toLowerCase().startsWith("image/") || /\.(avif|gif|heic|heif|jpe?g|png|webp)$/i.test(file.name);
}

/** Mirrors the API's own predicate (services/attachmentUploads.ts): an image
 * whose kind is the default one — post-#1174 that word is `other`, the
 * successor of the retired `'file'`. A member who files an image as a receipt
 * or a manual has said it is a document, and it leaves the photo gallery. */
export function isAssetPhotoAttachment(attachment: Attachment): boolean {
  return (attachment.collectionType ?? defaultAttachmentCollectionType) === defaultAttachmentCollectionType
    && attachment.mimeType.toLowerCase().startsWith("image/");
}

/**
 * A document about the thing, as opposed to a picture of it (#1176).
 *
 * The exact complement of `isAssetPhotoAttachment` within an entity's
 * attachments, minus photographed manual pages, which are neither: they are
 * images grouped into a collection and the Documents & manuals section shows
 * them as one manual rather than as loose rows.
 *
 * Deliberately phrased as "not a photo" rather than "is a PDF". A member who
 * files a photographed receipt under `receipt` has said it is a document, and
 * it has to leave the gallery and appear here — which is the whole reason the
 * upload asks for a kind. Testing the mime type instead would put it in both
 * places, or neither.
 */
export function isDocumentAttachment(attachment: Attachment): boolean {
  return !isAssetPhotoAttachment(attachment) && !isManualPageAttachment(attachment);
}

/** Documents newest first, which is the order a member looks for the thing
 * they just uploaded. */
export function sortDocumentAttachments(attachments: Attachment[]): Attachment[] {
  return attachments
    .filter(isDocumentAttachment)
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt) || right.id - left.id);
}

/** The kind an attachment is filed under, as a value the label lookup can key
 * on. A row from before #1174's migration, or one whose kind the API did not
 * send, reads as the default — the same thing the column says. */
export function documentKindOf(attachment: Attachment): DocumentCollectionType {
  const kind = attachment.collectionType ?? defaultAttachmentCollectionType;
  return (documentCollectionTypes as readonly string[]).includes(kind)
    ? kind as DocumentCollectionType
    : defaultAttachmentCollectionType;
}

/** Re-file a document under a different kind (#1176). Returns the fresh DTO,
 * so a caller never has to reconstruct the row it just changed. */
export function updateAttachmentCollectionType(
  attachmentId: number,
  collectionType: DocumentCollectionType
): Promise<Attachment> {
  return patchJson<Attachment>(`/api/v1/attachments/${attachmentId}/collection-type`, { collectionType });
}

/** Ask the pipeline to read a document again (#1176). Only offered where
 * `extraction.retryable` is true; the server re-checks and refuses otherwise. */
export function retryAttachmentExtraction(attachmentId: number): Promise<Attachment> {
  return postJson<Attachment>(`/api/v1/attachments/${attachmentId}/extraction-retry`, {});
}

export function sortAssetPhotoAttachments(attachments: Attachment[]): Attachment[] {
  return attachments
    .filter(isAssetPhotoAttachment)
    .sort((left, right) => {
      const leftOrder = left.collectionOrder && left.collectionOrder > 0 ? left.collectionOrder : Number.MAX_SAFE_INTEGER;
      const rightOrder = right.collectionOrder && right.collectionOrder > 0 ? right.collectionOrder : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.id - right.id;
    });
}

export function firstImageFileIndex(files: File[]): number | null {
  const index = files.findIndex(isImageFile);
  return index >= 0 ? index : null;
}

export async function uploadAssetFiles(
  assetId: EntityReferenceId,
  files: File[],
  options: { limit?: number; collectionType?: AttachmentCollectionType } = {}
): Promise<AttachmentUploadBatch> {
  const collectionType = options.collectionType ?? defaultAttachmentCollectionType;
  const outcomes = await runWithConcurrency(files, (file) =>
    api<Attachment>(`/api/v1/assets/${assetId}/attachments`, {
      method: "POST",
      body: attachmentUploadBody(file, collectionType)
    }), options);
  return toUploadBatch(outcomes);
}

export function linkAssetPhotos(
  assetId: EntityReferenceId,
  attachmentIds: number[],
  primaryAttachmentId?: number | null
): Promise<{ asset: Asset; attachments: Attachment[] }> {
  return api<{ asset: Asset; attachments: Attachment[] }>(`/api/v1/assets/${assetId}/photo-links`, {
    method: "POST",
    body: JSON.stringify({ attachmentIds, primaryAttachmentId })
  });
}

export function uploadedPhotoAttachmentIds(attachments: Attachment[]): number[] {
  return attachments.filter(isAssetPhotoAttachment).map((attachment) => attachment.id);
}

/**
 * Which attachment should be the cover, given the file the user picked.
 *
 * Takes the INDEX-ALIGNED results (#522), not the compacted success list.
 * Uploads run concurrently and settle independently, so a compacted array
 * shifts every position when an earlier file fails — picking file 2 as the
 * cover would silently promote file 3's photo. A failed pick falls back to the
 * first photo that did upload.
 */
export function primaryAttachmentIdForQueuedUpload(
  results: Array<Attachment | null>,
  primaryFileIndex: number | null
): number | null {
  const selectedAttachment = primaryFileIndex == null ? null : results[primaryFileIndex] ?? null;
  if (selectedAttachment && isAssetPhotoAttachment(selectedAttachment)) return selectedAttachment.id;
  return results.find((entry): entry is Attachment => entry != null && isAssetPhotoAttachment(entry))?.id ?? null;
}

/** Persist the photo order (and optionally which photo is the cover) for an
 * asset. `setAssetCover` is the same endpoint with a cover-first signature. */
export function reorderAssetPhotos(
  assetId: EntityReferenceId,
  orderedAttachmentIds: number[],
  primaryAttachmentId?: number | null
): Promise<Asset> {
  return patchJson<Asset>(`/api/v1/assets/${assetId}/photo-settings`, {
    orderedAttachmentIds,
    primaryAttachmentId
  });
}

export function setAssetCover(assetId: EntityReferenceId, orderedAttachmentIds: number[], coverAttachmentId: number): Promise<Asset> {
  return reorderAssetPhotos(assetId, orderedAttachmentIds, coverAttachmentId);
}

export async function uploadManualPages(assetId: EntityReferenceId, files: File[], label: string): Promise<void> {
  const body = new FormData();
  const trimmedLabel = label.trim();
  if (trimmedLabel) body.append("label", trimmedLabel);
  for (const file of files) body.append("pages", file);
  await api<{ collectionId: string; collectionLabel: string; attachments: Attachment[] }>(`/api/v1/assets/${assetId}/manual-pages`, { method: "POST", body });
}
